import { useCallback } from "react";

import { Container, Dialog, Flex, Portal } from "@chakra-ui/react";
import { createOverlay } from "@chakra-ui/react";
import { useFormSubmitError } from "@features/errors/form-errors";
import { useOtpSession } from "@features/two-fa/hooks/use-otp-session";
import { ClientGateway } from "@kex/api";
import { useIntl } from "react-intl";

import { SubPageTemplate } from "@shared/ui/templates/sub-page-template";

import { twoFaFeatureMessages } from "./messages";
import { OtpForm, type OtpFormValues } from "./otp-form";

export interface OTOPOverlayProps {
  description: string;
  initialData: ClientGateway.OtpModel;
  resend: (otp: ClientGateway.OtpModel) => Promise<ClientGateway.OtpModel>;
  submit: (code: string, otp: ClientGateway.OtpModel) => Promise<void>;
}

export const otpOverlay = createOverlay<OTOPOverlayProps>((props) => {
  const { description, initialData, resend, submit, onOpenChange, ...rest } =
    props;
  const { formatMessage } = useIntl();
  const { blockedUntil, otp, onResend } = useOtpSession({
    initialOtp: initialData,
    resend,
  });

  const handleVerify = useCallback(
    async ({ code }: OtpFormValues) => {
      await submit(code, otp);
    },
    [otp, submit],
  );

  const handleVerifyWithSubmitErrors = useFormSubmitError(handleVerify);

  const handleBack = useCallback(() => {
    onOpenChange?.({ open: false });
  }, [onOpenChange]);

  return (
    <Dialog.Root {...rest} modal={false} size="full">
      <Portal>
        <Dialog.Positioner top="16">
          <Dialog.Content backgroundColor="bg" boxShadow="none">
            <Flex direction="column">
              <Container
                as={Flex}
                flex={1}
                flexDirection="column"
                maxW="lg"
                pt="8"
              >
                <SubPageTemplate
                  title={formatMessage(twoFaFeatureMessages.title, {
                    br: <br />,
                  })}
                  onBack={handleBack}
                >
                  <OtpForm
                    deliveryContact={otp.deliveryContact}
                    kind={otp.deliveryType}
                    resendBlockedUntil={blockedUntil}
                    onResend={onResend}
                    onSubmit={handleVerifyWithSubmitErrors}
                  />
                </SubPageTemplate>
              </Container>
            </Flex>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
});
